import * as geoprocessor from "@arcgis/core/rest/geoprocessor.js";
import FeatureSet from "@arcgis/core/rest/support/FeatureSet.js";
import Graphic from "@arcgis/core/Graphic";
import Polygon from "@arcgis/core/geometry/Polygon";
import Point from "@arcgis/core/geometry/Point";
import {
  locationDetailsTask,
  removalReportTask,
  polygonFields,
  treeFields,
  treeFieldNames,
  reportEndpoint,
  exportShapefileTask,
  offsetReportTask,
} from "../lib/gpConfig";
import {
  Patch,
  RevegetationArea,
  Tree,
  treeCmType,
  treeType,
} from "../lib/types";

const treePropertyMapping = {
  type: { [treeType.Patch]: "pt", [treeType.Scattered]: "st" },
  cmType: { [treeCmType.CIR]: "cir", [treeCmType.DBH]: "dbh" },
};

const getPolygonFeaturesSet = (features: any[]) =>
  new FeatureSet({
    features: features.map(
      (x) =>
        new Graphic({
          geometry: new Polygon(x.geom),
          attributes: { id: x.id, type: x.type },
        })
    ),
    fields: polygonFields,
  });

const getTreesFeaturesSet = (trees: Tree[]) =>
  new FeatureSet({
    features: trees.map(
      (x) =>
        new Graphic({
          geometry: new Point(x.geom),
          attributes: {
            id: x.id,
            [treeFieldNames.type]: treePropertyMapping.type[x.type],
            [treeFieldNames.cmType]: treePropertyMapping.cmType[x.cmType],
            [treeFieldNames.cm]: x.cm,
            [treeFieldNames.vegCode]: x.vegCode,
            [treeFieldNames.benchmark]: x.benchmark,
          },
        })
    ),
    fields: treeFields,
  });

export const exportShapefile = async (
  patches: Patch[],
  trees: Tree[],
  revegAreas: RevegetationArea[]
) => {
  const patchesFeatureSet = getPolygonFeaturesSet(patches);
  const treesFeatureSet = getTreesFeaturesSet(trees);
  const revegAreasFeatureSet = getPolygonFeaturesSet(revegAreas);
  const result = await geoprocessor.execute(exportShapefileTask, {
    patches: patchesFeatureSet.toJSON(),
    trees: treesFeatureSet.toJSON(),
    revegetationAreas: revegAreasFeatureSet.toJSON(),
  });
  return result;
};

const fetchLocationDetails = async (
  patches: FeatureSet,
  trees: FeatureSet,
  revegAreas: FeatureSet | undefined
) => {
  const result = await geoprocessor.execute(locationDetailsTask, {
    patches: patches.toJSON(),
    trees: trees.toJSON(),
    revegetationAreas: revegAreas ? revegAreas.toJSON() : [],
  });
  return result;
};

const fetchRemovalDetails = async (
  patches: FeatureSet,
  trees: FeatureSet,
  pastRemovalArea: number | undefined
) => {
  const result = await geoprocessor.execute(removalReportTask, {
    patches: patches.toJSON(),
    trees: trees.toJSON(),
    pastRemovalArea: pastRemovalArea,
    includeOffsetResults: true,
  });
  return result;
};

const fetchOffsetDetails = async (
  patches: FeatureSet,
  trees: FeatureSet,
  revegetationAreas: FeatureSet
) => {
  const result = await geoprocessor.execute(offsetReportTask, {
    patches: patches.toJSON(),
    trees: trees.toJSON(),
    revegetationAreas: revegetationAreas.toJSON(),
    includeGainResults: true,
  });
  return result;
};

export const fetchRemovalSummary = async (
  patches: Patch[],
  trees: Tree[],
  pastRemovalArea: number | undefined
) => {
  const patchesFeatureSet = getPolygonFeaturesSet(patches);
  const treesFeatureSet = getTreesFeaturesSet(trees);
  const revegAreasFeatureSet = getPolygonFeaturesSet([]);

  return Promise.all([
    fetchLocationDetails(
      patchesFeatureSet,
      treesFeatureSet,
      revegAreasFeatureSet
    ),
    fetchRemovalDetails(patchesFeatureSet, treesFeatureSet, pastRemovalArea),
  ]);
};

export const fetchOffsetSummary = async (
  patches: Patch[],
  trees: Tree[],
  revegAreas: RevegetationArea[]
) => {
  const patchesFeatureSet = getPolygonFeaturesSet(patches);
  const treesFeatureSet = getTreesFeaturesSet(trees);
  const revegAreasFeatureSet = getPolygonFeaturesSet(revegAreas);

  return Promise.all([
    fetchLocationDetails(
      patchesFeatureSet,
      treesFeatureSet,
      revegAreasFeatureSet
    ),
    fetchOffsetDetails(
      patchesFeatureSet,
      treesFeatureSet,
      revegAreasFeatureSet
    ),
  ]);
};

export const requestRemovalReport = async (
  patches: Patch[],
  trees: Tree[],
  pastRemovalArea: number | undefined,
  appRequirements: any
) => {
  const patchesFeatureSet = getPolygonFeaturesSet(patches);
  const treesFeatureSet = getTreesFeaturesSet(trees);
  const result = await fetch(reportEndpoint + "/removal", {
    method: "POST",
    mode: "cors",
    body: JSON.stringify({
      patches: patchesFeatureSet.toJSON(),
      trees: treesFeatureSet.toJSON(),
      pastRemovalArea: pastRemovalArea,
      ...appRequirements,
    }),
  });
  return await result.json();
};

export const requestOffsetReport = async (
  patches: Patch[],
  trees: Tree[],
  revegetationAreas: RevegetationArea[]
) => {
  const patchesFeatureSet = getPolygonFeaturesSet(patches);
  const treesFeatureSet = getTreesFeaturesSet(trees);
  const revegetationAreasFeatureSet = getPolygonFeaturesSet(revegetationAreas);
  const result = await fetch(reportEndpoint + "/offset", {
    method: "POST",
    mode: "cors",
    body: JSON.stringify({
      patches: patchesFeatureSet.toJSON(),
      trees: treesFeatureSet.toJSON(),
      revegetationAreas: revegetationAreasFeatureSet.toJSON(),
    }),
  });
  return await result.json();
};

export const checkReportStatus = async (reportId: string) => {
  const response = await fetch(`${reportEndpoint}/${reportId}`);
  return await response.json();
};

export const downloadReport = async (reportId: string, filename: string) => {
  const url = `${reportEndpoint}/${reportId}/download`;
  return downloadFile(url, filename);
};

export const downloadFile = async (url: string, filename: string) => {
  fetch(url)
    .then((response) => response.blob())
    .then((blob) => {
      // Create blob link to download
      const url = window.URL.createObjectURL(new Blob([blob]));
      const link: any = document.createElement("a");
      link.href = url;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
    });
};
